import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Search } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { apiPost } from '../../api/client';
import { Avatar } from '../ui/Avatar';
import { NotificationBell } from '../notifications/NotificationBell';

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  tasks: 'Assegnazioni',
  calendar: 'Calendario',
  clients: 'Clienti',
  events: 'Eventi',
  collaborators: 'Collaboratori',
  settings: 'Impostazioni',
  finance: 'Finanza',
  projects: 'Progetti',
  'external-photographers': 'Esterni',
  performances: 'Prestazioni',
};

const BREADCRUMB_LABELS_EXTRA: Record<string, string> = {
  profile: 'Profilo',
  users: 'Gestione Utenti',
};

export function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);

  const segments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = segments.map((seg, i) => {
    const label = BREADCRUMB_LABELS[seg] ?? BREADCRUMB_LABELS_EXTRA[seg] ?? (seg.startsWith('c') && seg.length >= 20 ? 'Dettaglio' : seg);
    const path = '/' + segments.slice(0, i + 1).join('/');
    return { label, path, isLast: i === segments.length - 1 };
  });

  const handleLogout = async () => {
    try { await apiPost('/auth/logout'); } catch { /* ignore */ }
    logout();
    window.location.href = '/login';
  };

  return (
    <header className="topbar-glass" style={{
      height: 'var(--topbar-height)',
      background: 'var(--glass-bg-navbar)',
      backdropFilter: 'blur(16px) saturate(180%)',
      WebkitBackdropFilter: 'blur(16px) saturate(180%)',
      borderBottom: '0.5px solid rgba(255,255,255,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 1.25rem',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      flexShrink: 0,
      gap: '0.5rem',
      willChange: 'transform, backdrop-filter',
    }}>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb">
        <ol style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          listStyle: 'none', margin: 0, padding: 0,
        }}>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.path}>
              {i > 0 && (
                <li style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>/</li>
              )}
              <li>
                {crumb.isLast ? (
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)' }}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          ))}
        </ol>
      </nav>

      {/* Search trigger */}
      <button
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
        aria-label="Ricerca globale"
        className="topbar-search"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-interactive)', padding: '0.375rem 0.75rem',
          cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)',
          transition: 'border-color var(--transition-interactive)',
        }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-primary)')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
      >
        <Search size={14} />
        <span className="topbar-search-label">Cerca…</span>
        <kbd className="topbar-search-kbd" style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 3, padding: '0 4px' }}>⌘K</kbd>
      </button>

      {/* User */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
        <NotificationBell />
        <button
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
            borderRadius: 'var(--radius-card)',
            transition: 'background var(--transition-interactive)',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          title="Vai al profilo"
        >
          <Avatar src={user?.avatarUrl} name={user?.name ?? 'U'} size={28} />
          <span className="topbar-username" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {user?.name ?? 'Admin'}
          </span>
        </button>

        <button
          onClick={handleLogout}
          aria-label="Logout"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-faint)', padding: '0.25rem',
            borderRadius: 'var(--radius-interactive)', display: 'flex',
            transition: 'color var(--transition-interactive)',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-faint)')}
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
